/**
 * Selo "Serviços Financeiros Asaas" — exigência regulatória do Playbook BaaS.
 *
 * Aplica o selo nas três variantes oficiais (positivo, negativo preto, negativo
 * branco) usando as URLs CDN do Asaas, com o ID do cliente Meiuca. Atualizações
 * de identidade visual são centralizadas pelo Asaas: este componente continua
 * apontando para os mesmos URLs.
 *
 * O selo deve aparecer em todos os fluxos financeiros — abertura de subconta,
 * comprovantes, extratos, checkout e e-mails — conforme a Resolução Conjunta
 * nº 16/2025 do BACEN.
 *
 * Por padrão o selo é clicável e abre asaas.com em nova aba com
 * `rel="noopener noreferrer"`, conforme orientação do playbook.
 */

import { cn } from '../lib/utils'

// Personalized per-account seal ID issued by Asaas for the Meiuca account.
// Do NOT replace with the generic Playbook reference ID — Asaas validates
// compliance through the CDN logs tied to this specific account ID.
const SEAL_ID = 'd0571294-37f8-4eb8-a4de-bb10a1f36914'

const SEAL_URLS = {
  positive: `https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Positivo.svg?id=${SEAL_ID}`,
  'negative-black': `https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Negativo-Preto.svg?id=${SEAL_ID}`,
  'negative-white': `https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Negativo-Branco.svg?id=${SEAL_ID}`
} as const

export type AsaasSealVariant = keyof typeof SEAL_URLS
export type AsaasSealSize = 'sm' | 'md' | 'lg'

const SIZE_DIMENSIONS: Record<AsaasSealSize, { width: number; height: number }> = {
  sm: { width: 120, height: 36 },
  md: { width: 160, height: 48 },
  lg: { width: 200, height: 60 }
}

export interface AsaasSealProps {
  /** Visual variant. Default: `positive` (use over light backgrounds). */
  variant?: AsaasSealVariant
  /** Visual size. Default: `md` (160×48px). */
  size?: AsaasSealSize
  /** When false, renders as a plain `<img>` without the link wrapper. Default: true. */
  clickable?: boolean
  className?: string
}

export function AsaasSeal({
  variant = 'positive',
  size = 'md',
  clickable = true,
  className
}: AsaasSealProps) {
  const dimensions = SIZE_DIMENSIONS[size]
  const src = SEAL_URLS[variant]

  const img = (
    <img
      src={src}
      alt="Serviços financeiros prestados pelo Asaas"
      width={dimensions.width}
      height={dimensions.height}
      style={{ display: 'inline-block' }}
      className={cn(!clickable && className)}
    />
  )

  if (!clickable) {
    return img
  }

  return (
    <a
      href="https://asaas.com"
      target="_blank"
      rel="noopener noreferrer"
      className={cn('inline-block', className)}
      aria-label="Saiba mais sobre o Asaas — processador dos pagamentos"
    >
      {img}
    </a>
  )
}
